import { AlertTriangle } from "lucide-react";
import { Card } from "../components/ui";
import { navigate } from "../lib/router";

function DraftBanner() {
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 mb-6 text-sm">
      <div className="flex items-start gap-2">
        <AlertTriangle className="size-4 text-amber-900 flex-shrink-0 mt-0.5" />
        <div className="text-amber-900">
          <strong>Entwurf — anwaltlich prüfen lassen bevor live.</strong> Dieser Text dient als
          Strukturvorlage. Für den echten Launch unbedingt von einem Anwalt mit Schwerpunkt
          Vertriebs- und Datenschutzrecht prüfen lassen, insbesondere die Provisionsregelungen,
          die Mehrtier-Struktur (§16 UWG) und die DSGVO-Vorgaben.
        </div>
      </div>
    </div>
  );
}

function PageShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="mx-auto w-full max-w-3xl gradient-radial-warm px-4 py-8">
      <button
        onClick={() => navigate("/")}
        className="mb-4 text-sm text-ink-muted hover:text-ink"
      >
        ← Zurück
      </button>
      <h1 className="text-2xl font-semibold tracking-tight mb-2">{title}</h1>
      <p className="text-xs text-ink-muted mb-6">
        Stand: {new Date().toLocaleDateString("de-DE")} · DP Connect
      </p>
      <DraftBanner />
      <Card className="prose prose-sm max-w-none">{children}</Card>
    </main>
  );
}

export function AGBPage() {
  return (
    <PageShell title="Nutzungsbedingungen (AGB)">
      <h2 className="text-base font-semibold">§1 Geltungsbereich</h2>
      <p className="text-sm">
        Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für die Nutzung der DP-Connect-
        Empfehlungs-Plattform (im Folgenden "Plattform") durch Empfehlungs-Agenten ("Agent",
        "Light-Agent", "Voll-Agent") gegenüber dem Betreiber.
      </p>

      <h2 className="text-base font-semibold mt-4">§2 Vertragsschluss</h2>
      <p className="text-sm">
        Mit der Registrierung als Agent über das Online-Formular kommt ein Empfehlungs-Vertrag
        zustande. Die Registrierung steht volljährigen natürlichen Personen mit Wohnsitz in der EU
        offen. Light-Agenten benötigen ausschließlich eine gültige Email-Adresse. Voll-Agenten
        müssen zusätzlich Bank- und Steuerdaten hinterlegen.
      </p>

      <h2 className="text-base font-semibold mt-4">§3 Empfehlungs-Provisionen</h2>
      <p className="text-sm">
        Provisionen werden ausschließlich für nachweislich vermittelte Bestellungen über
        dpconnect.de gezahlt. Die Höhe der Provision richtet sich nach dem Agent-Typ (Light /
        Voll) und der jeweils geltenden Tier-Stufe (1, 2 oder 3). Die genauen Sätze werden im
        Agent-Dashboard angezeigt.
      </p>
      <p className="text-sm">
        Provisionen werden zunächst als "reserviert" markiert. Nach Ablauf einer Reserve-Periode
        (standardmäßig 30 Tage, zur Absicherung gegen Rücksendungen / Refunds) wechselt der Status
        auf "freigegeben" und wird zur nächsten Auszahlung berücksichtigt.
      </p>

      <h2 className="text-base font-semibold mt-4">§4 Auszahlung</h2>
      <p className="text-sm">
        Auszahlungen erfolgen monatlich (oder bei Light-Agenten ab Erreichen der individuellen
        Auszahlungs-Schwelle) per Bank-Überweisung auf die im Konto hinterlegte IBAN. Mit jeder
        Auszahlung wird eine Gutschrift (für Voll-Agenten ggf. mit ausgewiesener Umsatzsteuer)
        bereitgestellt.
      </p>

      <h2 className="text-base font-semibold mt-4">§5 Rückforderung (Clawback)</h2>
      <p className="text-sm">
        Im Falle einer Stornierung oder Rückerstattung einer bereits provisionierten Bestellung
        wird die ausgezahlte Provision anteilig zurückgefordert. Die Verrechnung erfolgt mit der
        nächsten Auszahlung.
      </p>

      <h2 className="text-base font-semibold mt-4">§6 Pflichten des Agenten</h2>
      <ul className="text-sm">
        <li>Keine irreführende Werbung. Keine Spam-Mails / unaufgeforderten Newsletter.</li>
        <li>Keine Bewerbung außerhalb der eigenen, klar gekennzeichneten Empfehlung.</li>
        <li>Self-Referral (Empfehlung an sich selbst) ist nicht erlaubt und wird systemseitig blockiert.</li>
        <li>Keine Schneeball-System-Praktiken (§16 UWG).</li>
      </ul>

      <h2 className="text-base font-semibold mt-4">§7 Kündigung</h2>
      <p className="text-sm">
        Der Agent-Vertrag kann jederzeit von beiden Seiten ohne Frist beendet werden. Bei
        Kündigung werden bereits berechtigte aber noch nicht ausgezahlte Provisionen zur nächsten
        Auszahlung mitgenommen.
      </p>

      <h2 className="text-base font-semibold mt-4">§8 Haftung</h2>
      <p className="text-sm">
        Wir haften nur für Vorsatz und grobe Fahrlässigkeit. Für leichte Fahrlässigkeit haften
        wir nur bei Verletzung wesentlicher Vertragspflichten.
      </p>

      <h2 className="text-base font-semibold mt-4">§9 Schlussbestimmungen</h2>
      <p className="text-sm">
        Es gilt deutsches Recht. Gerichtsstand ist der Sitz des Betreibers. Änderungen dieser AGB
        werden den Agenten per Email mitgeteilt. Widerspricht der Agent nicht binnen vier Wochen,
        gelten die geänderten Bedingungen als angenommen.
      </p>
    </PageShell>
  );
}

export function DatenschutzPage() {
  return (
    <PageShell title="Datenschutzerklärung">
      <h2 className="text-base font-semibold">1. Verantwortlicher</h2>
      <p className="text-sm">
        Verantwortlich für die Datenverarbeitung im Zusammenhang mit der Empfehlungs-Plattform ist
        der Betreiber von dpconnect.de. Genaue Kontaktdaten siehe Impressum von dpconnect.de.
      </p>

      <h2 className="text-base font-semibold mt-4">2. Welche Daten wir erheben</h2>
      <ul className="text-sm">
        <li>Bei Registrierung: Email, Vor- und Nachname, optional Telefon</li>
        <li>Bei Voll-Agent-Upgrade: IBAN, BIC, USt-ID, Anschrift</li>
        <li>Beim Klick auf Empfehlungs-Links: anonyme Cookie-ID (UUID), IP-Adresse, User-Agent</li>
        <li>Bei jeder vermittelten Bestellung: Order-ID, Customer-ID, Bestellbetrag, Produkte</li>
      </ul>

      <h2 className="text-base font-semibold mt-4">3. Zwecke + Rechtsgrundlagen</h2>
      <p className="text-sm">
        Die Verarbeitung erfolgt zur Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO):
        Auszahlung der Provisionen, Steuer-Dokumentation, Betrugsprävention.
        Für die Cookie-basierte Zuordnung von Empfehlungen gilt §25 Abs. 1 TTDSG i.V.m.
        Art. 6 Abs. 1 lit. a DSGVO (Einwilligung beim ersten Klick).
      </p>

      <h2 className="text-base font-semibold mt-4">4. Empfänger</h2>
      <p className="text-sm">
        Steuerlich relevante Daten (Gutschriften) werden an unseren Buchhaltungs-Dienstleister
        easybill GmbH (Hauptstraße 79, 32791 Lage) übermittelt. Hosting der Plattform: Hetzner
        Online GmbH, Gunzenhausen. Wir geben keine personenbezogenen Daten an Dritte ohne
        Rechtsgrundlage weiter.
      </p>

      <h2 className="text-base font-semibold mt-4">5. Speicherdauer</h2>
      <p className="text-sm">
        Stammdaten von Agenten werden für die Dauer des Vertrags und anschließend 10 Jahre
        aufbewahrt (Aufbewahrungsfristen nach §147 AO).
        Klick-Cookies werden 365 Tage gespeichert. Audit-Logs werden 7 Jahre aufbewahrt.
      </p>

      <h2 className="text-base font-semibold mt-4">6. Deine Rechte</h2>
      <ul className="text-sm">
        <li>Auskunft (Art. 15)</li>
        <li>Berichtigung (Art. 16)</li>
        <li>Löschung (Art. 17, sofern keine Aufbewahrungspflichten entgegenstehen)</li>
        <li>Einschränkung (Art. 18)</li>
        <li>Datenübertragbarkeit (Art. 20)</li>
        <li>Widerspruch (Art. 21)</li>
        <li>Beschwerde bei einer Aufsichtsbehörde</li>
      </ul>
      <p className="text-sm">
        Zur Ausübung deiner Rechte sende eine Email an die im Impressum genannte Adresse.
      </p>

      <h2 className="text-base font-semibold mt-4">7. Cookies</h2>
      <p className="text-sm">
        Der Empfehlungs-Cookie <code>dp_aff</code> wird auf dpconnect.de gesetzt, wenn ein Besucher
        über einen Empfehlungs-Link kommt. Er enthält ausschließlich eine zufällige UUID und keine
        personenbezogenen Daten. Du kannst ihn jederzeit in deinem Browser löschen.
      </p>
    </PageShell>
  );
}
